/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Phone, PhoneIncoming, PhoneOutgoing, Voicemail, Users, Trash2, Save, X, RefreshCw, Star, ListChecks, Plus, UserPlus, ArrowRightLeft, Settings, Volume2, Clock, Calendar, Mic } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import SmartDialer from '../components/SmartDialer';
import NewApplicationNotificationSettings from '../components/NewApplicationNotificationSettings';  // v3.21-1707
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { canUsePhoneSystem, PHONE_UNAUTHORIZED_MESSAGE } from '../lib/phone_access';
import { VoicemailsPanel } from '../components/VoicemailsPanel';
import { WhoWillRingPanel } from '../components/WhoWillRingPanel';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  </div>
);

const normalizePhone = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (raw.startsWith('+')) return raw;
  return digits ? `+${digits}` : raw;
};

const formatPhoneDisplay = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return raw;
};

// v3.14-529: Temporary Global Bypass Component
const TempBypassControl = () => {
  const [status, setStatus] = useState({ active: false });
  const [remaining, setRemaining] = useState(0);
  const [enabling, setEnabling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/telephony/temp-bypass');
      setStatus(res.data);
      if (res.data.active && res.data.remaining_seconds > 0) {
        setRemaining(res.data.remaining_seconds);
      } else {
        setRemaining(0);
      }
    } catch {
      setStatus({ active: false });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          setStatus({ active: false });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining]);

  const enableBypass = async () => {
    setEnabling(true);
    try {
      const res = await api.post('/telephony/temp-bypass', { route_to: '+17578228317', duration_seconds: 60 });
      if (res.data.status === 'already_active') {
        setRemaining(res.data.remaining_seconds);
        setStatus({ active: true, ...res.data });
        toast.info(`Bypass already active — ${res.data.remaining_seconds}s remaining`);
      } else {
        setRemaining(60);
        setStatus({ active: true, ...res.data });
        toast.success('1-Minute Global Bypass ENABLED');
      }
    } catch (err) {
      toast.error('Failed to enable bypass');
    } finally {
      setEnabling(false);
    }
  };

  const disableBypass = async () => {
    try {
      await api.delete('/telephony/temp-bypass');
      setStatus({ active: false });
      setRemaining(0);
      toast.success('Global bypass disabled');
    } catch {
      toast.error('Failed to disable');
    }
  };

  return (
    <div className={`rounded-xl border p-5 ${status.active && remaining > 0 ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'}`} data-testid="temp-bypass-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneIncoming className={`h-4 w-4 ${status.active && remaining > 0 ? 'text-red-600' : 'text-slate-500'}`} />
          <h3 className="text-sm font-semibold text-slate-800">Temporary Global Bypass</h3>
          {status.active && remaining > 0 && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-mono font-bold animate-pulse">
              ACTIVE — {remaining}s
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status.active && remaining > 0 ? (
            <Button size="sm" variant="destructive" onClick={disableBypass} className="h-8 text-xs" data-testid="disable-temp-bypass-btn">
              <X className="h-3 w-3 mr-1" /> Disable Now
            </Button>
          ) : (
            <Button size="sm" onClick={enableBypass} disabled={enabling} className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="enable-temp-bypass-btn">
              {enabling ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Phone className="h-3 w-3 mr-1" />}
              Enable 1-Minute Bypass
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2">All incoming calls skip IVR and route directly to +1 (757) 822-8317 for 60 seconds. Auto-expires. One active at a time.</p>
      {status.active && remaining > 0 && (
        <div className="mt-3">
          <div className="w-full bg-red-200 rounded-full h-2">
            <div className="bg-red-600 h-2 rounded-full transition-all duration-1000" style={{ width: `${(remaining / 60) * 100}%` }} />
          </div>
          <p className="text-[10px] text-red-500 mt-1">Enabled by {status.enabled_by || 'Admin'}</p>
        </div>
      )}
    </div>
  );
};

// ─── Standalone Team Directory Table (ALL changes tracked locally, saved on button click) ───
const TeamDirectoryTable = ({ title, members, testId, companyKey, onSaveUsers, onTransfer, onDelete, onAddAgent, onRefresh, disabled }) => {
  const [dirty, setDirty] = useState({}); // { [userId]: { field: newValue, ... } }
  const [saving, setSaving] = useState(false);

  const markDirty = (userId, field, value) => {
    console.log(`[DIRTY] user=${userId} field=${field} value=${value}`);
    setDirty(prev => ({ ...prev, [userId]: { ...(prev[userId] || {}), [field]: value } }));
  };

  const getVal = (userId, field, original) => {
    return dirty[userId]?.[field] !== undefined ? dirty[userId][field] : (original ?? '');
  };

  const getBool = (userId, field, original) => {
    return dirty[userId]?.[field] !== undefined ? dirty[userId][field] : (original === true);
  };

  const dirtyCount = Object.values(dirty).reduce((sum, obj) => sum + Object.keys(obj).length, 0);
  const hasDirty = dirtyCount > 0;

  const handleSave = async () => {
    console.log('[SAVE] Button clicked. dirty_updates:', JSON.stringify(dirty));
    if (!hasDirty) {
      console.error('[SAVE] No dirty updates — nothing to save');
      return;
    }
    setSaving(true);
    try {
      await onSaveUsers(dirty);
      console.log('[SAVE] Complete — clearing dirty state');
      setDirty({});
    } catch (err) {
      console.error('[SAVE] Failed:', err);
      // DO NOT clear dirty on failure
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4" data-testid={testId}>
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">{title} ({members.length} users)</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant={hasDirty ? "destructive" : "secondary"}
            onClick={handleSave}
            disabled={saving || !hasDirty || disabled}
            size="sm"
            data-testid={`force-save-${testId}`}
            className={hasDirty ? 'animate-pulse' : ''}
          >
            {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            {hasDirty ? `Save All Changes (${dirtyCount})` : 'Save All Changes'}
          </Button>
          <Button variant="default" size="sm" onClick={() => onAddAgent(companyKey)} data-testid={`add-agent-${testId}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add Agent
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid={`refresh-${testId}`}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500" title="Ring order when no extension is entered (1 rings first)">Order</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500">Name</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500">Phone</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500">Ext</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500">Position</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500">Availability</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center">Active</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center">Routing</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center"><span className="flex items-center justify-center gap-1"><Star className="h-3 w-3" />Primary</span></th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center" title="AI answering agent — answers instantly, skips the press-1 live-answer check">AI Agent</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center" title="Agent must press 1 to accept a routed call (personal voicemail never counts as answered)">Press 1</th>
              <th className="px-3 py-2.5 text-xs font-medium text-slate-500 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-slate-400 text-sm">No agents assigned. Click "Add Agent" to create one.</td></tr>
            ) : [...members].sort((a, b) => (Number.isInteger(a.routing_priority) ? a.routing_priority : 1e4) - (Number.isInteger(b.routing_priority) ? b.routing_priority : 1e4)).map(m => {
              const isActive = getBool(m.id, 'user_status', m.user_status === 'active') 
                ? true : (dirty[m.id]?.user_status !== undefined ? dirty[m.id].user_status === 'active' : m.user_status === 'active');
              const routingOn = getBool(m.id, 'routing_enabled', m.routing_enabled);
              const isPrimary = getBool(m.id, 'primary_receiver', m.primary_receiver);
              const repStatus = getVal(m.id, 'rep_status', m.rep_status || 'available');
              const transferLabel = companyKey === 'eqf_hub' ? 'PTC Travel' : 'EQF Hub';
              const rowDirty = dirty[m.id] && Object.keys(dirty[m.id]).length > 0;
              return (
                <tr key={m.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${rowDirty ? 'bg-amber-50/60 border-l-2 border-l-amber-400' : ''}`} data-testid={`team-row-${m.id}`}>
                  <td className="px-3 py-1.5 w-[64px]">
                    <Input className="h-7 text-xs w-[52px]" type="number" min={1} placeholder="—"
                      value={getVal(m.id, 'routing_priority', m.routing_priority ?? '') ?? ''}
                      onChange={(e) => markDirty(m.id, 'routing_priority', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      disabled={disabled}
                      data-testid={`routing-priority-${m.id}`} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div>
                      <span className="text-xs font-medium text-slate-800">{m.full_name}</span>
                      <span className="text-[10px] text-slate-400 ml-1.5">{m.role?.replace('ptc_', '')}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 max-w-[140px]">
                    <Input className="h-7 text-xs" placeholder="+1XXXXXXXXXX"
                      value={getVal(m.id, 'phone', m.phone)}
                      onChange={(e) => markDirty(m.id, 'phone', e.target.value)}
                      disabled={disabled}
                      data-testid={`phone-${m.id}`} />
                  </td>
                  <td className="px-3 py-1.5 max-w-[80px]">
                    <Input className="h-7 text-xs" placeholder="Ext"
                      value={getVal(m.id, 'extension', m.extension)}
                      onChange={(e) => markDirty(m.id, 'extension', e.target.value)}
                      disabled={disabled}
                      data-testid={`ext-${m.id}`} />
                  </td>
                  <td className="px-3 py-1.5 max-w-[130px]">
                    <Input className="h-7 text-xs" placeholder="Position"
                      value={getVal(m.id, 'position', m.position)}
                      onChange={(e) => markDirty(m.id, 'position', e.target.value)}
                      disabled={disabled}
                      data-testid={`position-${m.id}`} />
                  </td>
                  <td className="px-3 py-2.5">
                    <Select value={repStatus} onValueChange={(v) => markDirty(m.id, 'rep_status', v)} disabled={disabled}>
                      <SelectTrigger className="h-7 text-[11px] w-[95px]" data-testid={`status-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch
                      checked={dirty[m.id]?.user_status !== undefined ? dirty[m.id].user_status === 'active' : m.user_status === 'active'}
                      onCheckedChange={(v) => markDirty(m.id, 'user_status', v ? 'active' : 'inactive')}
                      disabled={disabled}
                      data-testid={`active-toggle-${m.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch
                      checked={routingOn}
                      onCheckedChange={(v) => markDirty(m.id, 'routing_enabled', v)}
                      disabled={disabled}
                      data-testid={`routing-toggle-${m.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch
                      checked={isPrimary}
                      onCheckedChange={(v) => markDirty(m.id, 'primary_receiver', v)}
                      disabled={disabled}
                      data-testid={`primary-toggle-${m.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch
                      checked={getBool(m.id, 'is_ai_agent', m.is_ai_agent === true)}
                      onCheckedChange={(v) => markDirty(m.id, 'is_ai_agent', v)}
                      disabled={disabled}
                      data-testid={`ai-agent-toggle-${m.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch
                      checked={getBool(m.id, 'require_live_answer', m.require_live_answer === true)}
                      onCheckedChange={(v) => markDirty(m.id, 'require_live_answer', v)}
                      disabled={disabled || getBool(m.id, 'is_ai_agent', m.is_ai_agent === true)}
                      data-testid={`press1-toggle-${m.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => onTransfer(m.id, m.full_name, companyKey)} className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors" title={`Transfer to ${transferLabel}`} data-testid={`transfer-${m.id}`}>
                        <ArrowRightLeft className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onDelete(m.id, m.full_name)} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors" title="Remove agent" data-testid={`delete-agent-${m.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Page ───
export default function TelephonyPage() {
  const { user } = useAuth();
  // v3.18-1294 — hard access gate (evaluated after ALL hooks so React's
  // rules-of-hooks stay satisfied; render branches on it below).
  const _phoneAllowed = canUsePhoneSystem(user);
  const [activeTab, setActiveTab] = useState('calls');
  const [callLogs, setCallLogs] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, inbound: 0, outbound: 0, voicemails: 0 });
  const [callFilter, setCallFilter] = useState('all');
  const [vmFilter, setVmFilter] = useState('all');
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const [addAgentCompany, setAddAgentCompany] = useState('eqf_hub');
  const [newAgent, setNewAgent] = useState({ full_name: '', email: '', phone: '', position: '', extension: '', role: 'ptc_team_member' });
  const [addAgentMode, setAddAgentMode] = useState('existing'); // 'existing' or 'new'
  const [allUsers, setAllUsers] = useState([]); // all users in the system
  const [selectedUserId, setSelectedUserId] = useState('');

  // Config/settings state
  const [config, setConfig] = useState(null);
  const [configDirty, setConfigDirty] = useState({});
  const [vmConfigs, setVmConfigs] = useState([]);
  const [vmPosition, setVmPosition] = useState('general');
  const [vmGreeting, setVmGreeting] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingVm, setSavingVm] = useState(false);

  // Computed team lists (memoized to avoid re-creating on every render)
  const eqfUsers = useMemo(() => teamMembers.filter(m => {
    const cr = Array.isArray(m.company_routing) ? m.company_routing : [];
    return cr.includes('eqf_hub') || cr.length === 0;
  }), [teamMembers]);
  const ptcUsers = useMemo(() => teamMembers.filter(m => {
    const cr = Array.isArray(m.company_routing) ? m.company_routing : [];
    return cr.includes('ptc_travel');
  }), [teamMembers]);

  const fetchCallLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (callFilter !== 'all') params.set('type', callFilter);
      const res = await api.get(`/telephony/call-logs?${params}`);
      setCallLogs(res.data.logs || []);
      setStats(prev => ({ ...prev, total: res.data.total || 0, inbound: res.data.inbound_count || 0, outbound: res.data.outbound_count || 0 }));
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
  }, [callFilter]);

  const onVmTotal = useCallback((t) => setStats(prev => ({ ...prev, voicemails: t })), []);

  const fetchVoicemails = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (vmFilter === 'mine') params.set('mine', 'true');
      const res = await api.get(`/telephony/voicemails?${params}`);
      setVoicemails(res.data.voicemails || []);
      setStats(prev => ({ ...prev, voicemails: res.data.total || 0 }));
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
  }, [vmFilter]);

  const [teamLoading, setTeamLoading] = useState(true); // Disable toggles until loaded

  const fetchTeam = useCallback(async () => {
    setTeamLoading(true);
    try {
      const res = await api.get('/users');
      const users = res.data.users || res.data || [];
      // Include ANY user who has company_routing set (phone system users)
      // OR has a ptc_* role (legacy support)
      const phoneSystemUsers = users.filter(u => {
        const cr = Array.isArray(u.company_routing) ? u.company_routing : [];
        const hasPtcRole = ['ptc_admin', 'ptc_director', 'ptc_team_member', 'ptc_manager'].includes(u.role);
        const hasRouting = cr.length > 0;
        return hasPtcRole || hasRouting;
      });
      console.log("USER STATE FROM API:", phoneSystemUsers.map(u => ({ name: u.full_name, user_status: u.user_status, routing_enabled: u.routing_enabled, primary_receiver: u.primary_receiver, rep_status: u.rep_status, company_routing: u.company_routing })));
      setTeamMembers(phoneSystemUsers);
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
    setTeamLoading(false);
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/telephony/config');
      setConfig(res.data);
      setConfigDirty({});
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
  }, []);

  const fetchVmConfigs = useCallback(async () => {
    try {
      const res = await api.get('/telephony/voicemail-config');
      setVmConfigs(res.data.configs || []);
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCallLogs(), fetchVoicemails(), fetchTeam(), fetchConfig(), fetchVmConfigs()]).finally(() => setLoading(false));
  }, [fetchCallLogs, fetchVoicemails, fetchTeam, fetchConfig, fetchVmConfigs]);

  const handleBulkSaveUsers = async (dirtyUpdates) => {
    const entries = Object.entries(dirtyUpdates);
    console.log('[SAVE ALL] Starting. Users to update:', entries.length, 'dirty_updates:', JSON.stringify(dirtyUpdates));
    if (entries.length === 0) {
      toast.error('Save failed — no updates sent');
      console.error('[SAVE ALL] No entries in dirty_updates!');
      throw new Error('No updates');
    }
    let saved = 0;
    let failed = 0;
    for (const [userId, fields] of entries) {
      const updates = {};
      for (const [field, value] of Object.entries(fields)) {
        updates[field] = field === 'phone' && value ? normalizePhone(value) : value;
      }
      console.log(`[SAVE ALL] Saving user: ${userId}`, 'Payload:', JSON.stringify(updates));
      try {
        const resp = await api.put(`/users/${userId}`, updates);
        console.log(`[SAVE ALL] Success for ${userId}:`, resp.status);
        saved++;
      } catch (err) {
        console.error(`[SAVE ALL] FAILED for ${userId}:`, err?.response?.data || err.message);
        failed++;
      }
    }
    if (failed === 0) {
      toast.success(`Saved ${saved} user(s) successfully`);
    } else {
      toast.error(`Saved ${saved}, failed ${failed}`);
      throw new Error(`${failed} saves failed`);
    }
    // HARD REFRESH — replace full state from backend
    console.log('[SAVE ALL] Hard refreshing from GET /api/users');
    await fetchTeam();
  };

  // Toggle handlers removed — ALL changes go through "Save All Changes" button now

  const deleteCallLog = async (logId) => {
    try { await api.delete(`/telephony/call-logs/${logId}`); setCallLogs(prev => prev.filter(l => l.id !== logId)); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };


  const openAddAgent = async (companyKey) => {
    setAddAgentCompany(companyKey);
    setNewAgent({ full_name: '', email: '', phone: '', position: '', extension: '', role: 'ptc_team_member' });
    setAddAgentMode('existing');
    setSelectedUserId('');
    // Load all users to show who can be added
    try {
      const res = await api.get('/users');
      const users = res.data.users || res.data || [];
      setAllUsers(users);
    } catch (err) { console.error('[TelephonyPage] Error:', err?.message || err); }
    setAddAgentOpen(true);
  };

  // Users NOT already in the phone system (no company_routing)
  const availableUsers = useMemo(() => {
    const inPhoneSystem = new Set(teamMembers.map(m => m.id));
    return allUsers.filter(u => !inPhoneSystem.has(u.id) && u.full_name);
  }, [allUsers, teamMembers]);

  const handleAddExistingUser = async () => {
    if (!selectedUserId) { toast.error('Select a user'); return; }
    try {
      await api.put(`/users/${selectedUserId}`, {
        company_routing: [addAgentCompany],
        user_status: 'active',
        routing_enabled: true,
        rep_status: 'available',
      });
      const user = allUsers.find(u => u.id === selectedUserId);
      toast.success(`${user?.full_name || 'User'} added to phone system`);
      setAddAgentOpen(false);
      fetchTeam();
    } catch (err) {
      console.error('[ADD EXISTING] Error:', err?.response?.data);
      toast.error(err?.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleAddAgent = async () => {
    if (!newAgent.full_name || !newAgent.email) { toast.error('Name and email are required'); return; }
    try {
      const phone = newAgent.phone ? normalizePhone(newAgent.phone) : '';
      const res = await api.post('/users', {
        full_name: newAgent.full_name.trim(),
        email: newAgent.email.trim().toLowerCase(),
        phone: phone,
        position: newAgent.position.trim(),
        role: newAgent.role,
        company_id: teamMembers[0]?.company_id || '',
        password: process.env.REACT_APP_DEFAULT_AGENT_PASSWORD || 'changeme123',
      });
      const userId = res.data?.user?.id || res.data?.id;
      if (userId) {
        await api.put(`/users/${userId}`, {
          company_routing: [addAgentCompany],
          user_status: 'active',
          routing_enabled: true,
          rep_status: 'available',
          extension: newAgent.extension.trim() || undefined,
          phone: phone || undefined,
          position: newAgent.position.trim() || undefined,
        });
        console.log(`[ADD AGENT] User ${userId} configured: extension=${newAgent.extension}, company=${addAgentCompany}`);
      }
      toast.success(`Agent "${newAgent.full_name}" added`);
      setAddAgentOpen(false);
      fetchTeam();
    } catch (err) {
      console.error('[ADD AGENT] Error:', err?.response?.data);
      toast.error(err?.response?.data?.detail || 'Failed to add agent');
    }
  };

  const handleDeleteAgent = async (userId, name) => {
    if (!window.confirm(`Remove agent "${name}" from the system? This will deactivate their account.`)) return;
    try {
      await api.put(`/users/${userId}`, { user_status: 'inactive', routing_enabled: false });
      setTeamMembers(prev => prev.filter(m => m.id !== userId));
      toast.success(`${name} removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove agent');
    }
  };

  const handleTransferAgent = async (userId, name, fromCompany) => {
    const toCompany = fromCompany === 'eqf_hub' ? 'ptc_travel' : 'eqf_hub';
    const toLabel = toCompany === 'ptc_travel' ? 'Prestigious Travel Club' : 'EQF Hub';
    if (!window.confirm(`Transfer "${name}" to ${toLabel}?`)) return;
    try {
      await api.put(`/users/${userId}`, { company_routing: [toCompany] });
      setTeamMembers(prev => prev.map(m => m.id === userId ? { ...m, company_routing: [toCompany] } : m));
      toast.success(`${name} transferred to ${toLabel}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Transfer failed');
    }
  };

  const updateConfigField = (field, value) => {
    setConfigDirty(prev => ({ ...prev, [field]: value }));
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/telephony/config', configDirty);
      toast.success('Settings saved');
      await fetchConfig();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSavingConfig(false);
  };

  const saveVmGreeting = async () => {
    if (!vmGreeting.trim()) { toast.error('Enter greeting text'); return; }
    setSavingVm(true);
    try {
      await api.put(`/telephony/voicemail-config/${vmPosition}`, { greeting: vmGreeting });
      toast.success(`${vmPosition} voicemail greeting saved`);
      await fetchVmConfigs();
      setVmGreeting('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSavingVm(false);
  };

  const getConfigVal = (field) => configDirty[field] !== undefined ? configDirty[field] : (config?.[field] ?? '');
  const getIvrVal = (field) => configDirty[field] !== undefined ? configDirty[field] : (config?.ivr?.[field] ?? '');
  const getScheduleVal = (field) => configDirty[field] !== undefined ? configDirty[field] : (config?.call_mode?.[field] ?? false);

  const formatDate = (iso) => {
    if (!iso) return '--';
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return '--'; }
  };

  const brandLabel = (brand) => {
    if (!brand) return '--';
    const map = { eqf: 'EQF', ptc: 'PTC Hub', travel_club: 'Travel Club', travel_club_sales: 'TC Sales', travel_club_contracts: 'TC Contracts', travel_club_travel: 'TC Travel' };
    return map[brand] || brand.toUpperCase();
  };

  return (
    !_phoneAllowed ? (
      <div className="max-w-lg mx-auto mt-16 rounded-md border border-slate-200 bg-white p-6 text-center" data-testid="telephony-unauthorized">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Phone system unavailable</h2>
        <p className="text-sm text-slate-600">{PHONE_UNAUTHORIZED_MESSAGE}</p>
      </div>
    ) : (
    <div className="space-y-4" data-testid="telephony-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Phone System</h1>
          <p className="text-sm text-slate-500 mt-0.5">Call routing, voicemail, and team control center</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchCallLogs(); fetchVoicemails(); fetchTeam(); fetchConfig(); fetchVmConfigs(); }} data-testid="refresh-all">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Business Hours + Holiday Mode Control Bar */}
      {config && (
        <div className="flex flex-wrap items-center gap-6 p-4 bg-white rounded-xl border border-slate-200" data-testid="routing-control-bar">
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-slate-700">Business Hours Mode</span>
            <Switch
              checked={getScheduleVal('business_hours')}
              onCheckedChange={(v) => { updateConfigField('business_hours', v); api.put('/telephony/config', { business_hours: v }).then(() => { toast.success(v ? 'Business hours ON' : 'After-hours mode active'); fetchConfig(); }).catch(() => toast.error('Failed')); }}
              data-testid="business-hours-toggle"
            />
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getScheduleVal('business_hours') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {getScheduleVal('business_hours') ? 'ON — Normal Routing' : 'OFF — After Hours'}
            </span>
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-red-500" />
            <span className="text-sm font-medium text-slate-700">Holiday Mode</span>
            <Switch
              checked={getScheduleVal('holiday')}
              onCheckedChange={(v) => { updateConfigField('holiday', v); api.put('/telephony/config', { holiday: v }).then(() => { toast.success(v ? 'Holiday mode ON — all routing overridden' : 'Holiday mode OFF'); fetchConfig(); }).catch(() => toast.error('Failed')); }}
              data-testid="holiday-mode-toggle"
            />
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getScheduleVal('holiday') ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
              {getScheduleVal('holiday') ? 'ON — Holiday Override' : 'OFF'}
            </span>
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-3" title="Human agents must press 1 to take a routed call, so a personal voicemail is never treated as answered. AI agents are exempt.">
            <Star className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">Live-answer check (press 1)</span>
            <Switch
              checked={config.agent_routing?.require_live_answer_confirmation === true}
              onCheckedChange={(v) => api.put('/telephony/config', { require_live_answer_confirmation: v }).then(() => { toast.success(v ? 'Live-answer check ON' : 'Live-answer check OFF'); fetchConfig(); }).catch(() => toast.error('Failed'))}
              data-testid="live-answer-check-toggle"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">Ring each agent</span>
            <Input type="number" min={8} max={60} className="h-7 w-[64px] text-xs"
              defaultValue={config.agent_routing?.ring_timeout_seconds ?? 18}
              onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n && n !== config.agent_routing?.ring_timeout_seconds) api.put('/telephony/config', { hunt_ring_timeout_seconds: n }).then(() => { toast.success(`Ring timeout ${n}s`); fetchConfig(); }).catch((err) => toast.error(err?.response?.data?.detail || 'Failed')); }}
              data-testid="ring-timeout-input" />
            <span className="text-xs text-slate-500">sec</span>
          </div>
          <div className="flex items-center gap-2" title="Rings after every routed agent has been tried. If it is not answered, the caller goes to Hub voicemail.">
            <span className="text-sm font-medium text-slate-700">If no one answers, forward to</span>
            <Input type="tel" className="h-7 w-[140px] text-xs" placeholder="Hub voicemail only"
              defaultValue={config.agent_routing?.final_forward_number || ''}
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (config.agent_routing?.final_forward_number || '')) api.put('/telephony/config', { final_forward_number: v }).then(() => { toast.success(v ? 'Final forwarding number saved' : 'Final forwarding cleared'); fetchConfig(); }).catch((err) => toast.error(err?.response?.data?.detail || 'Failed')); }}
              data-testid="final-forward-number-input" />
            <span className="text-xs text-slate-500">require press 1</span>
            <Switch
              checked={config.agent_routing?.final_forward_require_press1 === true}
              onCheckedChange={(v) => api.put('/telephony/config', { final_forward_require_press1: v }).then(() => { toast.success(v ? 'Final destination must press 1' : 'Final destination press 1 OFF'); fetchConfig(); }).catch(() => toast.error('Failed'))}
              data-testid="final-forward-press1-toggle"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Calls" value={stats.total} icon={Phone} color="bg-slate-700" />
        <StatCard label="Inbound" value={stats.inbound} icon={PhoneIncoming} color="bg-blue-600" />
        <StatCard label="Outbound" value={stats.outbound} icon={PhoneOutgoing} color="bg-emerald-600" />
        <StatCard label="Voicemails" value={stats.voicemails} icon={Voicemail} color="bg-amber-600" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calls" data-testid="tab-calls">Call Logs</TabsTrigger>
          <TabsTrigger value="voicemails" data-testid="tab-voicemails">Voicemails</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team Directory</TabsTrigger>
          <TabsTrigger value="dialer" data-testid="tab-dialer" className="flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" />Smart Dialer</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="flex items-center gap-1.5"><Settings className="h-3.5 w-3.5" />Voice & IVR Settings</TabsTrigger>
          {/* v3.21-1634 · Block 3 · Call Recordings link (INTERNAL only,
              backend also denies external partner roles). */}
          <TabsTrigger value="recordings" data-testid="tab-recordings"
                       onClick={() => { window.location.href = '/phone-system/recordings'; }}
                       className="flex items-center gap-1.5">
            <Voicemail className="h-3.5 w-3.5" />Call Recordings
          </TabsTrigger>
        </TabsList>

        {/* ── Call Logs ── */}
        <TabsContent value="calls">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <Select value={callFilter} onValueChange={setCallFilter}>
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="call-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calls</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Type</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Caller</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Called</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Brand</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500">Date</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-slate-500 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {callLogs.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">No call logs</td></tr>
                  ) : callLogs.map(log => (
                    <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`call-log-${log.id}`}>
                      <td className="px-4 py-2.5">
                        {log.type === 'inbound' ? <PhoneIncoming className="h-4 w-4 text-blue-500" /> : <PhoneOutgoing className="h-4 w-4 text-emerald-500" />}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-700">{log.caller || log.rep_name || '--'}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-700">{log.called || log.client_name || log.client_number || '--'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                          log.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                          log.status === 'voicemail' ? 'bg-amber-50 text-amber-700' :
                          log.status === 'ringing' ? 'bg-blue-50 text-blue-700' :
                          log.status === 'failed' ? 'bg-red-50 text-red-700' :
                          'bg-slate-50 text-slate-600'
                        }`}>{log.status || 'unknown'}</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{brandLabel(log.ivr_selection || log.call_as)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-2.5">
                        <button onClick={() => deleteCallLog(log.id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`delete-call-${log.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ── Voicemails ── */}
        <TabsContent value="voicemails">
          <VoicemailsPanel onTotal={onVmTotal} />
        </TabsContent>

        {/* ── Team Directory ── */}
        <TabsContent value="team">
          <TeamDirectoryTable
            title="EQF Hub / PTC Client Hub"
            members={eqfUsers}
            testId="team-table-eqf"
            companyKey="eqf_hub"
            onSaveUsers={handleBulkSaveUsers}
            onTransfer={handleTransferAgent}
            onDelete={handleDeleteAgent}
            onAddAgent={openAddAgent}
            onRefresh={fetchTeam}
            disabled={teamLoading}
          />
          <TeamDirectoryTable
            title="Prestigious Travel Club"
            members={ptcUsers}
            testId="team-table-ptc"
            companyKey="ptc_travel"
            onSaveUsers={handleBulkSaveUsers}
            onTransfer={handleTransferAgent}
            onDelete={handleDeleteAgent}
            onAddAgent={openAddAgent}
            onRefresh={fetchTeam}
            disabled={teamLoading}
          />
          {['ptc_admin', 'ptc_director'].includes(user?.role) && (
            <WhoWillRingPanel refreshKey={JSON.stringify([teamMembers, config?.agent_routing])} />
          )}
          <div className="text-[10px] text-slate-400 mt-2 px-2">
            <strong>Routing rules:</strong> Calls route to Active + Available + Routing ON + valid phone + assigned to caller's company. Primary rings first.
          </div>

          {/* Add Agent Dialog */}
          <Dialog open={addAgentOpen} onOpenChange={setAddAgentOpen}>
            <DialogContent className="sm:max-w-md" data-testid="add-agent-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Add to {addAgentCompany === 'ptc_travel' ? 'Prestigious Travel Club' : 'EQF Hub / PTC Client Hub'}
                </DialogTitle>
              </DialogHeader>

              {/* Mode tabs */}
              <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
                <button
                  onClick={() => setAddAgentMode('existing')}
                  className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${addAgentMode === 'existing' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="add-mode-existing"
                >
                  Add Existing User
                </button>
                <button
                  onClick={() => setAddAgentMode('new')}
                  className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${addAgentMode === 'new' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="add-mode-new"
                >
                  Create New Agent
                </button>
              </div>

              {addAgentMode === 'existing' ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Select User</Label>
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger className="mt-1" data-testid="select-existing-user">
                        <SelectValue placeholder="Choose a user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableUsers.length === 0 ? (
                          <SelectItem value="_none" disabled>No users available</SelectItem>
                        ) : availableUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name} — {u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedUserId && selectedUserId !== '_none' && (() => {
                    const u = allUsers.find(x => x.id === selectedUserId);
                    return u ? (
                      <div className="text-xs text-slate-500 bg-slate-50 rounded p-2 space-y-0.5">
                        <div><strong>Name:</strong> {u.full_name}</div>
                        <div><strong>Email:</strong> {u.email}</div>
                        <div><strong>Phone:</strong> {u.phone || 'Not set'}</div>
                        <div><strong>Role:</strong> {u.role?.replace('ptc_', '').replace('company_', '')}</div>
                      </div>
                    ) : null;
                  })()}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddAgentOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddExistingUser} disabled={!selectedUserId} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-add-existing">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add to Phone System
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Full Name *</Label>
                    <Input value={newAgent.full_name} onChange={e => setNewAgent(p => ({ ...p, full_name: e.target.value }))} placeholder="John Smith" data-testid="new-agent-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={newAgent.email} onChange={e => setNewAgent(p => ({ ...p, email: e.target.value }))} placeholder="john@company.com" data-testid="new-agent-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={newAgent.phone} onChange={e => setNewAgent(p => ({ ...p, phone: e.target.value }))} placeholder="+1XXXXXXXXXX" data-testid="new-agent-phone" />
                  </div>
                  <div>
                    <Label className="text-xs">Position</Label>
                    <Input value={newAgent.position} onChange={e => setNewAgent(p => ({ ...p, position: e.target.value }))} placeholder="Account Executive" data-testid="new-agent-position" />
                  </div>
                  <div>
                    <Label className="text-xs">Extension (3 digits)</Label>
                    <Input value={newAgent.extension} onChange={e => setNewAgent(p => ({ ...p, extension: e.target.value.replace(/\D/g, '').slice(0, 3) }))} placeholder="105" maxLength={3} data-testid="new-agent-extension" />
                  </div>
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={newAgent.role} onValueChange={v => setNewAgent(p => ({ ...p, role: v }))}>
                      <SelectTrigger data-testid="new-agent-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ptc_admin">Admin</SelectItem>
                        <SelectItem value="ptc_director">Director</SelectItem>
                        <SelectItem value="ptc_manager">Manager</SelectItem>
                        <SelectItem value="ptc_team_member">Team Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddAgentOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddAgent} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-add-agent">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Create Agent
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ── Smart Dialer ── */}
        <TabsContent value="dialer">
          <SectionErrorBoundary label="Smart Dialer" testId="smart-dialer-error">
            <SmartDialer />
          </SectionErrorBoundary>
        </TabsContent>

        {/* ── Voice & IVR Settings ── */}
        <TabsContent value="settings">
          <div className="space-y-4" data-testid="voice-ivr-settings">

            {/* v3.21-1707 — New Application Text Notifications (phone_system_admin only, backend-gated) */}
            <NewApplicationNotificationSettings />

            {/* Voice Engine */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="voice-engine-section">
              <div className="flex items-center gap-2 mb-4">
                <Volume2 className="h-4 w-4 text-indigo-600" />
                <h3 className="text-sm font-semibold text-slate-800">TTS Voice Engine</h3>
                <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">Global — all IVR, voicemails, and prompts</span>
              </div>
              <Select value={configDirty.voice || config?.ivr?.voice || 'Polly.Joanna-Neural'} onValueChange={(v) => updateConfigField('voice', v)}>
                <SelectTrigger className="w-[280px]" data-testid="voice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config?.ivr?.supported_voices || ['Polly.Joanna-Neural', 'Polly.Matthew-Neural', 'Polly.Salli-Neural', 'Polly.Kendra-Neural', 'Polly.Joey-Neural', 'Polly.Ivy-Neural', 'Polly.Ruth-Neural', 'Polly.Stephen-Neural']).map(v => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* IVR Greetings */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ivr-greetings-section">
              <div className="flex items-center gap-2 mb-4">
                <Mic className="h-4 w-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-slate-800">IVR Greetings</h3>
                <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Text → TTS voice on call</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Main IVR Greeting</Label>
                  <Textarea
                    value={configDirty.ivr_greeting_number_1 !== undefined ? configDirty.ivr_greeting_number_1 : (config?.ivr?.ivr_greeting || '')}
                    onChange={(e) => updateConfigField('ivr_greeting_number_1', e.target.value)}
                    placeholder="Thank you for calling. Please hold while we connect you..."
                    className="min-h-[100px] text-sm"
                    data-testid="ivr-main-greeting"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">EQF / PTC Hub Greeting</Label>
                  <Textarea
                    value={configDirty.eqf_greeting !== undefined ? configDirty.eqf_greeting : (config?.ivr?.eqf_greeting || '')}
                    onChange={(e) => updateConfigField('eqf_greeting', e.target.value)}
                    placeholder="Thank you for calling EasyQuickFund and PTC Client Hub..."
                    className="min-h-[100px] text-sm"
                    data-testid="ivr-eqf-greeting"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Prestigious Travel Club Greeting</Label>
                  <Textarea
                    value={configDirty.ptc_greeting !== undefined ? configDirty.ptc_greeting : (config?.ivr?.ptc_greeting || '')}
                    onChange={(e) => updateConfigField('ptc_greeting', e.target.value)}
                    placeholder="Thank you for calling Prestigious Travel Club..."
                    className="min-h-[100px] text-sm"
                    data-testid="ivr-ptc-greeting"
                  />
                </div>
              </div>
            </div>

            {/* v3.14-529: Company Phone Assignments */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="company-phone-assignments">
              <div className="flex items-center gap-2 mb-4">
                <Phone className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-slate-800">Company Phone Numbers</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-800 w-64">EasyQuickFund / PTC Client Hub</span>
                  <span className="text-sm font-mono text-blue-700">{config?.numbers?.number_1 ? formatPhoneDisplay(config.numbers.number_1) : 'Not set'}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-800 w-64">Prestigious Travel Club</span>
                  <span className="text-sm font-mono text-blue-700">{config?.numbers?.number_2 ? formatPhoneDisplay(config.numbers.number_2) : 'Not set'}</span>
                </div>
              </div>
            </div>

            {/* v3.14-529: Temporary Global Bypass */}
            <TempBypassControl />

            {/* v3.14-529: IVR Bypass Numbers */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ivr-bypass-section">
              <div className="flex items-center gap-2 mb-4">
                <PhoneIncoming className="h-4 w-4 text-green-600" />
                <h3 className="text-sm font-semibold text-slate-800">IVR Bypass Numbers</h3>
                <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full">Skip IVR, direct connect</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">When a caller matches a bypass number, the IVR is skipped and the call routes directly. Supports Google Voice and mobile numbers.</p>
              <div className="space-y-4">
                {(config?.bypass_companies || []).map((company) => (
                  <div key={company.id} className="border rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{company.name}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500 uppercase mb-1 block">Bypass Numbers (comma separated)</Label>
                        <Input
                          defaultValue={(company.bypass_numbers || []).join(', ')}
                          placeholder="+17571234567, +14075551234"
                          className="text-xs"
                          data-testid={`bypass-numbers-${company.id}`}
                          onBlur={async (e) => {
                            const nums = e.target.value.split(',').map(n => n.trim()).filter(Boolean);
                            try {
                              await api.put(`/telephony/bypass-numbers/${company.id}`, {
                                bypass_numbers: nums,
                                bypass_route_to: company.bypass_route_to || '',
                              });
                              toast.success(`Bypass numbers updated for ${company.name}`);
                            } catch (err) {
                              toast.error('Failed to update bypass numbers');
                            }
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500 uppercase mb-1 block">Route To (phone number)</Label>
                        <Input
                          defaultValue={company.bypass_route_to || ''}
                          placeholder="+17578228317"
                          className="text-xs"
                          data-testid={`bypass-route-${company.id}`}
                          onBlur={async (e) => {
                            const routeTo = e.target.value.trim();
                            const inputEl = document.querySelector(`[data-testid="bypass-numbers-${company.id}"]`);
                            const nums = inputEl ? inputEl.value.split(',').map(n => n.trim()).filter(Boolean) : (company.bypass_numbers || []);
                            try {
                              await api.put(`/telephony/bypass-numbers/${company.id}`, {
                                bypass_numbers: nums,
                                bypass_route_to: routeTo,
                              });
                              toast.success(`Bypass route updated for ${company.name}`);
                            } catch (err) {
                              toast.error('Failed to update bypass route');
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(!config?.bypass_companies || config.bypass_companies.length === 0) && (
                  <p className="text-xs text-slate-400 italic">No companies found. Add companies first.</p>
                )}
              </div>
            </div>

            {/* After Hours + Holiday Greetings */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="scheduling-greetings-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-slate-800">After Hours & Holiday Greetings</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">After Hours Greeting</Label>
                  <Textarea
                    value={configDirty.after_hours_message !== undefined ? configDirty.after_hours_message : (config?.call_mode?.after_hours_message || '')}
                    onChange={(e) => updateConfigField('after_hours_message', e.target.value)}
                    placeholder="Our office is currently closed. Please leave a message and we'll return your call during business hours..."
                    className="min-h-[100px] text-sm"
                    data-testid="after-hours-greeting"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Holiday Greeting</Label>
                  <Textarea
                    value={configDirty.holiday_message !== undefined ? configDirty.holiday_message : (config?.call_mode?.holiday_message || '')}
                    onChange={(e) => updateConfigField('holiday_message', e.target.value)}
                    placeholder="Happy holidays! Our office is closed for the holiday. Please leave a message..."
                    className="min-h-[100px] text-sm"
                    data-testid="holiday-greeting"
                  />
                </div>
              </div>
            </div>

            {/* Save All Settings Button */}
            <div className="flex justify-end">
              <Button
                onClick={saveConfig}
                disabled={savingConfig || Object.keys(configDirty).length === 0}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
                data-testid="save-all-settings"
              >
                {savingConfig ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save All Settings {Object.keys(configDirty).length > 0 && `(${Object.keys(configDirty).length} changes)`}
              </Button>
            </div>

            {/* Voicemail Greeting Control */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="voicemail-greeting-section">
              <div className="flex items-center gap-2 mb-4">
                <Voicemail className="h-4 w-4 text-orange-600" />
                <h3 className="text-sm font-semibold text-slate-800">Voicemail Greetings</h3>
                <span className="text-[10px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">Uses same TTS voice as IVR</span>
              </div>

              {/* Existing voicemail configs */}
              {vmConfigs.length > 0 && (
                <div className="mb-4">
                  <Label className="text-xs text-slate-500 mb-2 block">Current Greetings</Label>
                  <div className="space-y-2">
                    {vmConfigs.map(vc => (
                      <div key={vc.position} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg text-xs">
                        <span className="font-medium text-slate-700 w-20 capitalize">{vc.position}</span>
                        <span className="text-slate-600 flex-1">{vc.greeting}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-end gap-3">
                <div className="w-40">
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Position</Label>
                  <Select value={vmPosition} onValueChange={(v) => { setVmPosition(v); const existing = vmConfigs.find(c => c.position === v); setVmGreeting(existing?.greeting || ''); }}>
                    <SelectTrigger data-testid="vm-position-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="closer">Closer</SelectItem>
                      <SelectItem value="setter">Setter</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-medium text-slate-600 mb-1 block">Greeting Message</Label>
                  <Input
                    value={vmGreeting}
                    onChange={(e) => setVmGreeting(e.target.value)}
                    placeholder="You've reached our voicemail. Please leave a message after the tone..."
                    data-testid="vm-greeting-input"
                  />
                </div>
                <Button onClick={saveVmGreeting} disabled={savingVm || !vmGreeting.trim()} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-vm-greeting">
                  {savingVm ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save Greeting
                </Button>
              </div>
            </div>

          </div>
        </TabsContent>

      </Tabs>
    </div>
    )
  );
}
